import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { createEditor, Transforms } from 'slate';
import { Slate, Editable, withReact } from 'slate-react';
import './TagTextField.css';
import { resolveTagName } from '../utils/tagDisplay';

const createEmptyParagraph = () => [{ type: 'paragraph', children: [{ text: '' }] }];

const normalizeSlateValue = (value) => {
    if (!Array.isArray(value) || value.length === 0) {
        return createEmptyParagraph();
    }
    return value;
};

export default function TagTextField({
    value = '',
    onChange,
    dir = 'ltr',
    disabled = false,
    style = {},
    tagMap = {}
}) {
    const editor = useMemo(() => {
        const e = withReact(createEditor());
        const { isInline, isVoid } = e;
        e.isInline = element => (element.type === 'tag' ? true : isInline(element));
        e.isVoid = element => (element.type === 'tag' ? true : isVoid(element));
        return e;
    }, []);

    // Parse/normalize the incoming value to a valid Slate document
    const [val, setVal] = useState(() => normalizeSlateValue(stringToSlate(value)));

    useEffect(() => {
        const slateVal = normalizeSlateValue(stringToSlate(value));
        setVal(slateVal);
    }, [value]);

    // Use a stable key so Slate remounts cleanly when the doc shape changes
    const valKey = useMemo(() => {
        try { return JSON.stringify(val); } catch { return 'doc'; }
    }, [val]);

    const renderElement = useCallback(({ attributes, children, element }) => {
        if (element.type === 'tag') {
            const { tag, open } = element;
            const tagName = resolveTagName(tagMap, tag);
            const type = open ? 'open' : 'close';
            const typeLabel = open ? 'opening' : 'closing';
            const capitalizedTypeLabel = typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1);
            const ariaLabel = tagName
                ? `${tagName} ${typeLabel} tag ${tag}`
                : `${typeLabel} tag ${tag}`;
            const titleText = tagName
                ? `${tagName} (${typeLabel} ${tag})`
                : `${capitalizedTypeLabel} tag ${tag}`;

            return (
                <span
                    {...attributes}
                    data-tag={tag}
                    data-type={type}
                    data-tag-name={tagName || ''}
                    title={titleText}
                    aria-label={ariaLabel}
                    contentEditable={false}
                    draggable
                    onDragStart={e => {
                        e.dataTransfer.setData('text/tag', tag);
                        e.dataTransfer.setData('text/type', type);
                    }}
                    className="inline-tag"
                >
                    <span className="tag-pill" aria-hidden="true">{tag}</span>
                </span>
            );
        }
        return <p {...attributes}>{children}</p>;
    }, [tagMap]);

    const handleDrop = useCallback(
        e => {
            if (!e.dataTransfer.types.includes('text/tag')) return;
            e.preventDefault();
            const tag = e.dataTransfer.getData('text/tag');
            const type = e.dataTransfer.getData('text/type');
            if (!tag) return;
            const node = { type: 'tag', tag, open: type === 'open', children: [{ text: '' }] };
            Transforms.insertNodes(editor, node);
        },
        [editor]
    );

    const onSlateChange = useCallback(
        v => {
            const nextValue = normalizeSlateValue(v);
            setVal(nextValue);
            if (onChange) onChange(slateToString(nextValue));
        },
        [onChange]
    );

    return (
        <div dir={dir} style={style} className="tag-text-field">
            {/* ✅ Use initialValue, not value */}
            <Slate key={valKey} editor={editor} initialValue={val} onChange={onSlateChange}>
                <Editable
                    renderElement={renderElement}
                    readOnly={disabled}
                    spellCheck={false}
                    onDrop={handleDrop}
                    onDragOver={e => {
                        if (e.dataTransfer.types.includes('text/tag')) e.preventDefault();
                    }}
                />
            </Slate>
        </div>
    );
}

function stringToSlate(text) {
    if (typeof text !== 'string') return createEmptyParagraph();
    text = text
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&');

    const paragraphs = text.split('\n');

    const createChildrenFromText = paragraphText => {
        const regex = /<\/?\d+>/g;
        let last = 0;
        const children = [];
        let match;
        while ((match = regex.exec(paragraphText)) !== null) {
            if (match.index > last) {
                children.push({ text: paragraphText.slice(last, match.index) });
            }
            const tagText = match[0];
            const tag = tagText.replace(/[<>/]/g, '');
            const open = !tagText.startsWith('</');
            children.push({ type: 'tag', tag, open, children: [{ text: '' }] });
            last = match.index + tagText.length;
        }
        if (last < paragraphText.length) {
            children.push({ text: paragraphText.slice(last) });
        }
        if (children.length === 0) children.push({ text: '' });
        return children;
    };

    const nodes = paragraphs.map(paragraphText => ({
        type: 'paragraph',
        children: createChildrenFromText(paragraphText),
    }));

    if (nodes.length === 0) {
        return createEmptyParagraph();
    }

    return nodes;
}

function slateToString(value) {
    const nodeToString = node => {
        if (node.type === 'tag') return node.open ? `<${node.tag}>` : `</${node.tag}>`;
        if (node.text) return node.text;
        return (node.children || []).map(n => nodeToString(n)).join('');
    };

    if (!Array.isArray(value)) {
        return '';
    }

    const paragraphs = value.map(node => {
        if (node.type === 'paragraph') {
            return (node.children || []).map(child => nodeToString(child)).join('');
        }
        return nodeToString(node);
    });

    return paragraphs.join('\n');
}

export { stringToSlate, slateToString };
